import { relegateCountForGroup } from '@h2h/shared';
import { useOpenProfile } from '../../../hooks/useOpenProfile';
import { zoneForRank } from '../../../lib/round';
import { teamGradient } from '../../../lib/teams';
import { cn } from '../../../lib/cn';
import type { Standing } from './types';
import { PromotionLine } from './PromotionLine';
import { RelegationLine } from './RelegationLine';

export function DivisionRows({
  standings,
  uid,
  promote,
  divisionSize
}: {
  standings: Standing[];
  uid: string | null;
  promote: number;
  divisionSize: number;
}) {
  const openProfile = useOpenProfile();
  const relegate = relegateCountForGroup(Math.max(divisionSize, standings.length));
  const sorted = [...standings].sort((a, b) => a.rankInGroup - b.rankInGroup);
  const meIdx = sorted.findIndex((s) => s.userId === uid);
  const start = meIdx >= 0 ? Math.max(0, Math.min(meIdx - 2, Math.max(0, sorted.length - 5))) : 0;
  const window = sorted.slice(start, start + 6);
  const size = Math.max(divisionSize, standings.length);

  return (
    <>
      {window.map((s, i) => {
        const you = s.userId === uid;
        const prev = window[i - 1];
        const zone = zoneForRank(s.rankInGroup, size);
        const showPromoCut = prev != null && prev.rankInGroup <= promote && s.rankInGroup > promote;
        const showRelegCut =
          relegate > 0 &&
          prev != null &&
          prev.rankInGroup <= size - relegate &&
          s.rankInGroup > size - relegate;
        return (
          <div key={s.userId}>
            {showPromoCut && <PromotionLine promote={promote} />}
            {showRelegCut && <RelegationLine relegate={relegate} />}
            <button
              type="button"
              onClick={() => openProfile(s.userId, you)}
              className={cn(
                'tap flex w-full items-center gap-3 px-[15px] py-3 text-left',
                you && 'bg-night',
                !you && zone === 'promotion' && 'bg-accent/[0.04]',
                !you && zone === 'relegation' && 'bg-danger/[0.04]'
              )}
            >
              <span
                className={cn(
                  'num w-[18px] text-center text-[13px] font-bold',
                  you && 'font-extrabold text-white',
                  !you && zone === 'promotion' && 'font-extrabold text-accent-ink',
                  !you && zone === 'relegation' && 'font-extrabold text-danger',
                  !you && zone === 'middle' && 'text-ink2'
                )}
              >
                {s.rankInGroup}
              </span>
              <div
                className={cn(
                  'grid h-[30px] w-[30px] place-items-center rounded-full text-[10px] font-extrabold text-white',
                  you && 'border-[1.5px] border-accent'
                )}
                style={{ backgroundImage: teamGradient(s.displayName) }}
              >
                {s.displayName.slice(0, 2).toUpperCase()}
              </div>
              <span
                className={cn(
                  'flex-1 truncate text-[14px] tracking-snug',
                  you ? 'font-extrabold text-white' : 'font-semibold text-ink'
                )}
              >
                {s.displayName}
                {you && (
                  <span className="ml-1.5 rounded-[5px] bg-accent px-1.5 py-px text-[10px] font-extrabold text-[#06371F]">
                    YOU
                  </span>
                )}
              </span>
              <span className={cn('num text-[14px] font-extrabold', you ? 'text-white' : 'text-ink')}>
                {s.roundScore.toLocaleString()}
              </span>
            </button>
          </div>
        );
      })}
    </>
  );
}
